const normaliseName = value => String(value || '').toLowerCase()
  .replace(/\bcenter\b/g, 'centre').replace(/\bdriving\b/g, '')
  .replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');

export const isTestCentreQuery = query => /\btest[\s-]+cent(?:re|er)s?\b/i.test(String(query || ''));
export const destinationSearchProvider = query => isTestCentreQuery(query) ? 'ndg_test_centres' : 'google_places';

export function selectSpokenDestination(query, places) {
  const candidates = (Array.isArray(places) ? places : []).filter(place => place?.place_id);
  if (candidates.length === 1) return candidates[0];
  const exact = candidates.filter(place => normaliseName(place.name) === normaliseName(query));
  return exact.length === 1 ? exact[0] : null;
}

export async function runDestinationCommand({ query, search, preview, isCurrent, confirmation }) {
  if (!isCurrent()) return { ok: false, message: 'That lesson has changed. Try again in the active lesson.' };
  const places = await search(query);
  if (!isCurrent()) return { ok: false, message: 'That lesson has changed. No destination was selected.' };
  if (!Array.isArray(places) || !places.length) return { ok: false, message: 'No destination was selected. Check the map search result and try a more specific place.' };
  const place = selectSpokenDestination(query, places);
  if (!place) return { ok: false, message: 'I found more than one matching place. Choose the destination shown on the map.' };
  const route = await preview(place);
  if (!isCurrent()) return { ok: false, message: 'That lesson has changed. The requested route was not started.' };
  if (route?.status === 'RETURN_DEADLINE_AT_RISK') return { ok: false, message: 'That destination does not fit the agreed return time. The map shows the available route choices.' };
  if (route?.status !== 'OK') return { ok: false, message: 'The route preview could not complete. Check the lesson map for the reason.' };
  return confirmation.prepare(route.destination || place, route);
}

export function createDestinationConfirmation({ now = () => Date.now(), maxAgeMs = 90000 } = {}) {
  let pending = null;
  let revision = 0;
  const cancel = () => { pending = null; revision++; };
  return {
    cancel,
    prepare(place, preview) {
      cancel();
      pending = { place, preview, preparedAt: now() };
      const label = [place.name || 'That destination', place.address || place.formatted_address].filter(Boolean).join(', ');
      return { ok: true, pending_confirmation: true, message: `${label}. Shall I go?` };
    },
    async confirm({ preview, go, isCurrent }) {
      const selected = pending;
      pending = null; // one confirmation consumes exactly one proposal
      const commandRevision = revision;
      const current = () => isCurrent() && commandRevision === revision;
      if (!selected || !current() || now() - selected.preparedAt < 0 || now() - selected.preparedAt > maxAgeMs) {
        return { ok: false, message: 'There is no current destination awaiting confirmation. Tell me the destination again.' };
      }
      // Refresh GPS and the original lesson deadline before executing Go.
      const fresh = await preview(selected.place);
      if (!current()) return { ok: false, message: 'That destination request changed. No route was started.' };
      if (fresh?.status !== 'OK' || (fresh.destination?.place_id && fresh.destination.place_id !== selected.place.place_id)) {
        return { ok: false, message: 'That destination can no longer be confirmed within the agreed return time. Check the map and choose again.' };
      }
      const accepted = await go(fresh.destination || selected.place, fresh);
      if (!isCurrent()) return { ok: false, message: 'That lesson has changed. Check the current lesson map.' };
      return { ok: accepted === true, message: accepted === true
        ? 'The map route has updated. Follow its destination and return guidance.'
        : 'The route change did not complete. Check the lesson map for the reason.' };
    },
  };
}
